/**
 * 오늘의 돌봄 정보 카드 — DAU 재방문 훅 H1 (큐레이션 전환, v5.1.0).
 *
 * 영구지침: docs/develop/frontend/home-page-portal-spec.md §4.1
 * plan:    docs/plan/2026-05-04-today-insight-curation.md
 *
 * 슬롯 구성:
 *  1행 — 오늘의 돌봄 팁 (블로그)        → /blog/{slug}
 *  2행 — 오늘의 추천 시설 (평가등급 A·B) → /facilities/{id}
 *  3행 — 자가진단 CTA (정적)             → /guide/self-assessment
 *
 * 데이터 갱신: 매일 06:00 KST 일배치, TanStack Query staleTime 12h.
 */
import { Link } from 'react-router-dom'
import { Sparkles, ChevronRight, Building2, ClipboardCheck } from 'lucide-react'
import { useTodayCuration } from '@/features/home/api'

const GRADE_TONE: Record<string, string> = {
  A: 'bg-emerald-50 text-emerald-700',
  B: 'bg-sky-50 text-sky-700',
}

export default function TodayInsightCard() {
  const { data } = useTodayCuration()
  const tip = data?.tipPost ?? null
  const facility = data?.recommendedFacility ?? null
  const cta = data?.selfAssessmentCta

  return (
    <section className="rounded-2xl border border-cb-gray-100 bg-white p-5">
      <header className="mb-3 flex items-start justify-between">
        <h2 className="flex items-center gap-1.5 text-base font-bold text-cb-gray-900">
          <Sparkles size={16} className="text-amber-500" />
          오늘의 돌봄 정보
        </h2>
        <span className="text-[10px] text-cb-gray-400">갱신 06:00</span>
      </header>

      <ul className="flex flex-col divide-y divide-cb-gray-100">
        {tip && (
          <li>
            <Link
              to={`/blog/${tip.slug}`}
              className="-mx-1.5 flex items-center justify-between gap-2 rounded-lg px-1.5 py-2.5 hover:bg-cb-gray-50"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold text-cb-primary">
                  오늘의 돌봄 팁
                </p>
                <p className="mt-0.5 truncate text-sm font-bold text-cb-gray-900">
                  {tip.title}
                </p>
              </div>
              <ChevronRight size={16} className="flex-shrink-0 text-cb-gray-400" />
            </Link>
          </li>
        )}

        {facility && (
          <li>
            <Link
              to={`/facilities/${facility.id}`}
              className="-mx-1.5 flex items-center gap-2.5 rounded-lg px-1.5 py-2.5 hover:bg-cb-gray-50"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-cb-gray-100">
                {facility.thumbnailUrl ? (
                  <img
                    src={facility.thumbnailUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Building2 size={20} className="text-cb-gray-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold text-cb-primary">
                  오늘의 추천 시설
                </p>
                <p className="mt-0.5 truncate text-sm font-bold text-cb-gray-900">
                  {facility.name}
                </p>
                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-cb-gray-500">
                  {facility.addressSido && <span>{facility.addressSido}</span>}
                  {facility.evaluationGrade && (
                    <span
                      className={`rounded px-1 py-px text-[9px] font-bold ${
                        GRADE_TONE[facility.evaluationGrade] ?? 'bg-cb-gray-100 text-cb-gray-600'
                      }`}
                    >
                      등급 {facility.evaluationGrade}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={16} className="flex-shrink-0 text-cb-gray-400" />
            </Link>
          </li>
        )}
      </ul>

      {cta && (
        <Link
          to={cta.linkPath}
          className="mt-3 flex items-center gap-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-3 text-white hover:opacity-95"
        >
          <ClipboardCheck size={28} className="flex-shrink-0 text-white/85" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold">{cta.title}</p>
            <p className="truncate text-[11px] text-white/85">{cta.subtitle}</p>
          </div>
          <span className="flex-shrink-0 rounded-full bg-white/20 px-2.5 py-1 text-[10px] font-bold backdrop-blur-sm">
            {cta.buttonLabel}
          </span>
        </Link>
      )}
    </section>
  )
}
